import { addMetadata, getActiveInteraction } from '@atlaskit/react-ufo/interaction-metrics';

import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

type Interaction = NonNullable<ReturnType<typeof getActiveInteraction>>;
type Resolution = 'matched' | 'mismatched' | 'missingTitle' | 'failed';

type Counts = Record<
	| 'eligible'
	| 'matched'
	| 'mismatched'
	| 'missingTitle'
	| 'failed'
	| 'layoutMeasured'
	| 'layoutUnavailable'
	| 'rowIncreased'
	| 'rowDecreased',
	number
>;

type Collection = { counts: Counts; apps: Set<string> };
const collections = new WeakMap<Interaction, Collection>();
// Only the two supported experience names are cached; re-read the raw config for the off switch.
const configurations = new Map<string, { value: string; enabled: boolean }>();

export function isBylineTitleDiagnosticsEnabled(ufoName: string): boolean {
	if (!['view_page', 'live-edit'].includes(ufoName)) {
		return false;
	}
	try {
		const value = StatsigConfigurations.getString('confluence_cls_attribution_config', '{}');
		const cached = configurations.get(ufoName);
		if (cached?.value === value) {
			return cached.enabled;
		}
		// Reuse the decision until the config changes, preserving the in-flight off switch.
		const config = JSON.parse(value);
		const experienceName = ufoName === 'view_page' ? 'view-page' : ufoName;
		const included =
			experienceName === 'view-page'
				? (config?.includeEntries?.['view-page'] ?? config?.includeEntries?.view_page)
				: config?.includeEntries?.[experienceName];
		// Do not apply clsDetailsThreshold: low/zero-CLS loads are part of the denominator.
		const enabled =
			config?.bylineTitleDiagnostics === true &&
			included === true &&
			(config.loadType === undefined ||
				config.loadType === 'initial' ||
				config.loadType === 'both');
		configurations.set(ufoName, { value, enabled });
		return enabled;
	} catch {
		return false;
	}
}

export type BylineLayout = {
	width: number;
	height: number;
	rows: number;
	viewportWidth: number;
	viewportHeight: number;
};

// Only the small owning byline subtree is inspected, once before and once after a mismatch.
// This observes wrapping associated with the update, not causal attribution of a browser CLS entry.
export function measureBylineLayout(trigger: HTMLElement | null): BylineLayout | undefined {
	const root = trigger?.closest<HTMLElement>('[data-testid="byline-single-line"]');
	if (!root?.isConnected || root.ownerDocument.visibilityState === 'hidden') {
		return;
	}
	const triggerRect = trigger?.getBoundingClientRect();
	const rect = root.getBoundingClientRect();
	if (!triggerRect?.width || !triggerRect.height || !rect.width || !rect.height) {
		return;
	}
	// Include metadata, app triggers and the toggle, excluding the hidden SSR fallback.
	const items = [
		root.firstElementChild,
		...root.querySelectorAll(
			'[data-testid="forge-byline-header-wrapper"], [data-testid="web-item-link"], [data-testid="single-line-byline-toggle-wrapper"]',
		),
	];
	const tops = items
		.flatMap((item) => {
			const itemRect = item?.getBoundingClientRect();
			return itemRect?.width && itemRect.height ? [itemRect.top] : [];
		})
		.sort((a, b) => a - b);
	const rows: number[] = [];
	for (const top of tops) {
		// Same tolerance as SingleLineByLine's existing row detection: icons/text may align differently.
		if (rows.length === 0 || top - rows[rows.length - 1] >= 15) {
			rows.push(top);
		}
	}
	return {
		width: rect.width,
		height: rect.height,
		rows: rows.length,
		viewportWidth: window.innerWidth,
		viewportHeight: window.innerHeight,
	};
}

export type BylineTitleDiagnostic = {
	isActive: () => boolean;
	resolve: (result: Resolution) => boolean;
	recordLayout: (before: BylineLayout | undefined, after: BylineLayout | undefined) => void;
};

export function startBylineTitleDiagnostics(
	appId: string,
	contentId: string,
): BylineTitleDiagnostic | undefined {
	const interaction = getActiveInteraction();
	if (
		!interaction ||
		interaction.type !== 'page_load' ||
		!['view_page', 'live-edit'].includes(interaction.ufoName) ||
		!isBylineTitleDiagnosticsEnabled(interaction.ufoName)
	) {
		return;
	}
	let collection = collections.get(interaction);
	if (!collection) {
		collection = {
			counts: {
				eligible: 0,
				matched: 0,
				mismatched: 0,
				missingTitle: 0,
				failed: 0,
				layoutMeasured: 0,
				layoutUnavailable: 0,
				rowIncreased: 0,
				rowDecreased: 0,
			},
			apps: new Set(),
		};
		collections.set(interaction, collection);
	}
	// IDs are ephemeral deduplication keys, never emitted. Popup refreshes/remounts are not new samples.
	const key = JSON.stringify([appId, contentId]);
	if (collection.apps.has(key)) {
		return;
	}
	collection.apps.add(key);
	const { counts } = collection;
	const isActive = () =>
		getActiveInteraction()?.id === interaction.id &&
		isBylineTitleDiagnosticsEnabled(interaction.ufoName);
	const publish = () => {
		if (isActive()) {
			// Update one bounded object; do not append one UFO custom-data entry per app/update.
			addMetadata(interaction.id, { bylineTitleDiagnostics: { version: 1, ...counts } });
		}
	};
	counts.eligible++;
	publish();
	let resolution: Resolution | undefined;
	let layoutReported = false;
	return {
		isActive,
		resolve(result: Resolution): boolean {
			if (resolution || !isActive()) {
				return false;
			}
			resolution = result;
			counts[result]++;
			publish();
			return result === 'mismatched';
		},
		recordLayout(before: BylineLayout | undefined, after: BylineLayout | undefined) {
			if (resolution !== 'mismatched' || layoutReported || !isActive()) {
				return;
			}
			layoutReported = true;
			if (
				!before ||
				!after ||
				!before.rows ||
				!after.rows ||
				Math.abs(after.width - before.width) > 1 ||
				after.viewportWidth !== before.viewportWidth ||
				after.viewportHeight !== before.viewportHeight
			) {
				counts.layoutUnavailable++;
			} else {
				counts.layoutMeasured++;
				if (after.rows > before.rows && after.height > before.height + 1) {
					counts.rowIncreased++;
				} else if (after.rows < before.rows && after.height < before.height - 1) {
					counts.rowDecreased++;
				}
			}
			publish();
		},
	};
}
